/**
 * Server sync helpers. Local stores remain the UI source of truth;
 * these push/pull when /api is healthy and auth mode is server.
 */

import { apiHealth, apiJson, getApiBase } from "./client";
import { getAuthMode } from "../auth/authStore";
import type { MatchProfile } from "../matching/types";
import type { MatchRecord } from "../matching/types";
import type { FlowAnswers } from "../flow/steps";
import type { ProfileAnswers } from "../domain/answers";
import type { ChatMessage } from "../matching/interestTypes";

export function isServerMode(): boolean {
  return getAuthMode() === "server";
}

export async function serverAvailable(): Promise<boolean> {
  return (await apiHealth()) && isServerMode();
}

export async function submitProfileToServer(
  answers: FlowAnswers,
  profileAnswers: ProfileAnswers | undefined,
  opts: { submit: boolean }
): Promise<{ ok: true } | { ok: false; error: string }> {
  if (!(await serverAvailable())) {
    return { ok: false, error: "Server not available" };
  }
  const res = await apiJson<{ ok: boolean; error?: string }>("/api/profile", "PUT", {
    firstName: answers.firstName,
    age: answers.age ? Number(answers.age) : 25,
    city: answers.city || "",
    gender: answers.gender,
    profession: answers.profession || "",
    aboutYou: answers.aboutYou || "",
    lookingFor: answers.lookingFor || "",
    hobbies: answers.hobbies || "",
    scales: {
      loyalty: answers.scales.loyalty ?? 5,
      honesty: answers.scales.honesty ?? 5,
      passion: answers.scales.passion ?? 5,
      commitment: answers.scales.commitment ?? 5,
      // Stored on the profile. Not scored — valuesScore stays the four axes.
      social: answers.scales.social ?? null,
    },
    answers: profileAnswers ?? null,
    submit: opts.submit,
  });
  if (!res.ok) return { ok: false, error: res.error };
  return { ok: true };
}

export async function fetchServerDeck(): Promise<{
  profiles: MatchProfile[];
  reason?: string;
  reviewStatus?: string;
}> {
  if (!(await serverAvailable())) return { profiles: [] };
  const res = await apiJson<{
    profiles?: Array<MatchProfile & { photoUrls?: string[] }>;
    reason?: string;
    reviewStatus?: string;
  }>("/api/deck", "GET");
  if (!res.ok) return { profiles: [], reason: res.error };
  const profiles = (res.data.profiles || []).map((p) => ({
    ...p,
    source: "pool" as const,
    // photoUrls from API are same-origin private paths — stash on about for card if needed later
  }));
  return {
    profiles,
    reason: res.data.reason,
    reviewStatus: res.data.reviewStatus,
  };
}

export async function expressOnServer(
  toUserId: string,
  status: "liked" | "passed",
  valuesScore?: number
): Promise<{ match: MatchRecord | null; error?: string }> {
  if (!(await serverAvailable())) return { match: null };
  const res = await apiJson<{
    match?: {
      id: string;
      mutual: boolean;
      createdAt: string;
      valuesScore: number;
    } | null;
  }>("/api/likes", "POST", { toUserId, status, valuesScore });
  if (!res.ok) return { match: null, error: res.error };
  const m = res.data.match;
  if (!m?.mutual) return { match: null };
  return {
    match: {
      id: m.id,
      profileId: toUserId,
      matchedAt: m.createdAt,
      valuesScore: m.valuesScore,
      chatUnlocked: true,
    },
  };
}

export async function fetchServerMatches(): Promise<
  Array<{ record: MatchRecord; profile: MatchProfile }>
> {
  if (!(await serverAvailable())) return [];
  const res = await apiJson<{
    matches?: Array<{
      id: string;
      matchedAt: string;
      valuesScore: number;
      chatUnlocked: boolean;
      profile: MatchProfile;
    }>;
  }>("/api/matches", "GET");
  if (!res.ok) return [];
  return (res.data.matches || []).map((m) => ({
    record: {
      id: m.id,
      profileId: m.profile.id,
      matchedAt: m.matchedAt,
      valuesScore: m.valuesScore,
      chatUnlocked: m.chatUnlocked,
    },
    profile: { ...m.profile, source: "pool" as const },
  }));
}

export async function fetchMessages(matchId: string): Promise<ChatMessage[]> {
  if (!(await serverAvailable())) return [];
  const res = await apiJson<{
    messages?: Array<{
      id: string;
      match_id: string;
      from_user_id: string;
      body: string;
      created_at: string;
    }>;
  }>(`/api/matches/${encodeURIComponent(matchId)}/messages`, "GET");
  if (!res.ok) return [];
  return (res.data.messages || []).map((m) => ({
    id: m.id,
    matchId: m.match_id,
    fromUserId: m.from_user_id,
    body: m.body,
    createdAt: m.created_at,
  }));
}

export async function sendMessage(
  matchId: string,
  body: string
): Promise<
  | { ok: true; message: ChatMessage }
  | { ok: false; error: string }
> {
  if (!(await serverAvailable())) return { ok: false, error: "Server not available" };
  const res = await apiJson<{
    message?: {
      id: string;
      matchId: string;
      fromUserId: string;
      body: string;
      createdAt: string;
    };
    error?: string;
  }>(`/api/matches/${encodeURIComponent(matchId)}/messages`, "POST", { body });
  if (!res.ok || !res.data.message) {
    return { ok: false, error: (!res.ok ? res.error : null) || "Could not send message." };
  }
  const m = res.data.message;
  return {
    ok: true,
    message: {
      id: m.id,
      matchId: m.matchId,
      fromUserId: m.fromUserId,
      body: m.body,
      createdAt: m.createdAt,
    },
  };
}

export async function fetchModerationQueue(): Promise<unknown[]> {
  // Moderator cookie session — do not gate on authMode "server" alone.
  // Review console must load ticketed viewPaths whenever /api is up.
  if (!(await apiHealth())) return [];
  const res = await apiJson<{ items?: unknown[] }>("/api/moderation/queue", "GET");
  if (!res.ok) return [];
  return res.data.items || [];
}

export async function moderateUser(
  userId: string,
  decision: "approved" | "rejected" | "needs_resubmit",
  note?: string
): Promise<{ ok: true } | { ok: false; error: string }> {
  const res = await apiJson<{ ok: boolean }>(
    `/api/moderation/${encodeURIComponent(userId)}`,
    "POST",
    { decision, note }
  );
  if (!res.ok) return { ok: false, error: res.error };
  return { ok: true };
}

export function mediaAbsoluteUrl(viewPath: string): string {
  if (!viewPath) return "";
  if (viewPath.startsWith("http")) return viewPath;
  return `${getApiBase()}${viewPath}`;
}
